import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState, useCallback } from "react";
import { Flame, Wind, BookOpen, Music2, Sparkles, GraduationCap, NotebookPen, Sun, CalendarCheck, Trophy, Flower2 as Lotus, ChevronRight, Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { UserBadges } from "@/components/site/UserBadges";

export const Route = createFileRoute("/_authenticated/ascension")({
  head: () => ({
    meta: [
      { title: "Ascension Dashboard · KAfrequency" },
      { name: "description", content: "Track your meditation, breath, reading, activations, journal, and Lotus Levels of consciousness." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AscensionPage,
});




type PracticeKind = "meditation" | "breath" | "reading";
interface PracticeRow { id: string; kind: PracticeKind; minutes: number; logged_on: string; note: string | null; created_at: string }
interface JournalRow { id: string; title: string | null; body: string; created_at: string }
interface IntentionRow { id: string; intention: string; intention_date: string; updated_at: string }
interface ReviewRow { id: string; week_start: string; wins: string | null; lessons: string | null; focus_next: string | null; updated_at: string }
interface CourseRow { id: string; course_slug: string; course_title: string | null; completed_at: string }
interface CompletionRow { id: string; activation_slug: string; completed_at: string }
interface ListenRow { id: string; release_slug: string; song_slug: string | null; played_at: string }

// ---------- Lotus Level formula ----------
// XP model: 1 activation = 20 XP, 1 course = 100 XP, 1 practice minute = 2 XP,
// 1 journal = 15 XP, 1 daily intention = 5 XP, 1 weekly review = 30 XP,
// 1 track listened = 3 XP. Each level costs 1.4× the last.
const LOTUS_NAMES = [
  "Seed", "Root", "Sprout", "First Bloom", "Rising Sun", "Golden Petal",
  "Inner Fire", "Clear Mirror", "Steady Flame", "Sovereign Breath",
  "Silent Witness", "Awakened Heart", "Sacred Discipline", "Field Governor",
  "Radiant Body", "Living Ankh", "Solar Crown", "Ka Alignment",
  "Divine Current", "Fully Risen",
];

function lotusFor(xp: number) {
  let need = 100;
  let level = 1;
  let remain = xp;
  while (remain >= need && level < LOTUS_NAMES.length) {
    remain -= need;
    level += 1;
    need = Math.round(need * 1.4);
  }
  const name = LOTUS_NAMES[Math.min(level - 1, LOTUS_NAMES.length - 1)];
  const progress = level >= LOTUS_NAMES.length ? 1 : remain / need;
  return { level, name, progress: Math.max(0, Math.min(1, progress)), toNext: Math.max(0, need - remain) };
}

// ---------- streak helper ----------
function streak(dates: string[]): { current: number; longest: number } {
  if (dates.length === 0) return { current: 0, longest: 0 };
  const set = new Set(dates.map((d) => d.slice(0, 10)));
  const today = new Date();
  const iso = (d: Date) => d.toISOString().slice(0, 10);

  // current
  let cur = 0;
  const cursor = new Date(today);
  // allow today OR yesterday to seed streak
  if (!set.has(iso(cursor))) cursor.setUTCDate(cursor.getUTCDate() - 1);
  while (set.has(iso(cursor))) {
    cur += 1;
    cursor.setUTCDate(cursor.getUTCDate() - 1);
  }

  // longest
  const sorted = Array.from(set).sort();
  let longest = 0;
  let run = 0;
  let prev: Date | null = null;
  for (const s of sorted) {
    const d = new Date(s + "T00:00:00Z");
    if (prev && (d.getTime() - prev.getTime()) / 86400000 === 1) run += 1;
    else run = 1;
    if (run > longest) longest = run;
    prev = d;
  }
  return { current: cur, longest };
}

function weekStartIso(d = new Date()): string {
  const day = d.getUTCDay(); // 0=Sun
  const diff = (day + 6) % 7; // Monday start
  const monday = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - diff));
  return monday.toISOString().slice(0, 10);
}

function AscensionPage() {
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState<string>("");
  const [practices, setPractices] = useState<PracticeRow[]>([]);
  const [journals, setJournals] = useState<JournalRow[]>([]);
  const [intention, setIntention] = useState<IntentionRow | null>(null);
  const [review, setReview] = useState<ReviewRow | null>(null);
  const [courses, setCourses] = useState<CourseRow[]>([]);
  const [completions, setCompletions] = useState<CompletionRow[]>([]);
  const [listening, setListening] = useState<ListenRow[]>([]);

  const todayIso = new Date().toISOString().slice(0, 10);
  const week = weekStartIso();

  const load = useCallback(async () => {
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) return;
    const first = (u.user.user_metadata?.display_name as string | undefined) ?? u.user.email?.split("@")[0] ?? "Sovereign";
    setName(first);
    const [p, j, i, r, c, ac, l] = await Promise.all([
      supabase.from("practice_logs").select("*").order("logged_on", { ascending: false }).limit(500),
      supabase.from("journal_entries").select("id,title,body,created_at").order("created_at", { ascending: false }).limit(50),
      supabase.from("daily_intentions").select("*").eq("intention_date", todayIso).maybeSingle(),
      supabase.from("weekly_reviews").select("*").eq("week_start", week).maybeSingle(),
      supabase.from("course_completions").select("*").order("completed_at", { ascending: false }),
      supabase.from("activation_completions").select("id, activation_slug, completed_at").order("completed_at", { ascending: false }).limit(500),
      supabase.from("listening_history").select("id, release_slug, song_slug, played_at").order("played_at", { ascending: false }).limit(500),
    ]);
    setPractices((p.data as PracticeRow[]) ?? []);
    setJournals((j.data as JournalRow[]) ?? []);
    setIntention((i.data as IntentionRow | null) ?? null);
    setReview((r.data as ReviewRow | null) ?? null);
    setCourses((c.data as CourseRow[]) ?? []);
    setCompletions((ac.data as CompletionRow[]) ?? []);
    setListening((l.data as ListenRow[]) ?? []);
    setLoading(false);
  }, [todayIso, week]);

  useEffect(() => { void load(); }, [load]);

  const meditation = useMemo(() => practices.filter((p) => p.kind === "meditation"), [practices]);
  const breath = useMemo(() => practices.filter((p) => p.kind === "breath"), [practices]);
  const reading = useMemo(() => practices.filter((p) => p.kind === "reading"), [practices]);

  const medStreak = useMemo(() => streak(meditation.map((r) => r.logged_on)), [meditation]);
  const brStreak = useMemo(() => streak(breath.map((r) => r.logged_on)), [breath]);
  const rdStreak = useMemo(() => streak(reading.map((r) => r.logged_on)), [reading]);
  const activationStreak = useMemo(() => streak(completions.map((r) => r.completed_at)), [completions]);

  const totalMinutes = useMemo(() => practices.reduce((s, r) => s + (r.minutes || 0), 0), [practices]);
  const medMinutes = useMemo(() => meditation.reduce((s, r) => s + r.minutes, 0), [meditation]);
  const brMinutes = useMemo(() => breath.reduce((s, r) => s + r.minutes, 0), [breath]);
  const rdMinutes = useMemo(() => reading.reduce((s, r) => s + r.minutes, 0), [reading]);

  const xp =
    completions.length * 20 +
    courses.length * 100 +
    totalMinutes * 2 +
    journals.length * 15 +
    (intention ? 5 : 0) +
    (review ? 30 : 0) +
    listening.length * 3;
  const lotus = lotusFor(xp);

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Reading the field…</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20">
      {/* Header + Lotus Level */}
      <header className="relative overflow-hidden rounded-3xl border hairline bg-gradient-to-br from-charcoal via-obsidian to-charcoal p-8 sm:p-12">
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-gold/10 blur-3xl" aria-hidden />
        <p className="text-xs uppercase tracking-[0.35em] text-gold">Ascension Dashboard</p>
        <h1 className="mt-3 font-serif text-4xl leading-tight sm:text-5xl">
          Rise, {name}.
        </h1>
        <div className="mt-8 grid gap-6 sm:grid-cols-[auto_1fr] sm:items-center">
          <div className="flex items-center gap-4">
            <div className="relative flex h-20 w-20 items-center justify-center rounded-full border-2 border-gold/40 bg-obsidian">
              <Lotus className="text-gold-luminous" size={32} />
              <span className="absolute -bottom-1 rounded-full bg-gold px-2 py-0.5 text-[10px] font-semibold text-obsidian">
                Lv {lotus.level}
              </span>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Lotus Level</p>
              <p className="mt-1 font-serif text-2xl text-gold-luminous">{lotus.name}</p>
              <p className="mt-1 text-xs text-muted-foreground">{xp.toLocaleString()} XP · {lotus.toNext} to next bloom</p>
            </div>
          </div>
          <div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-white/5">
              <div
                className="h-full rounded-full bg-gradient-to-r from-gold via-gold-luminous to-gold transition-all"
                style={{ width: `${Math.round(lotus.progress * 100)}%` }}
              />
            </div>
            <p className="mt-2 text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Consciousness rising · {Math.round(lotus.progress * 100)}%</p>
          </div>
        </div>
      </header>

      {/* Daily intention + weekly review */}
      <section className="mt-10 grid gap-4 lg:grid-cols-2">
        <DailyIntentionCard intention={intention} onSaved={load} today={todayIso} />
        <WeeklyReviewCard review={review} onSaved={load} weekStart={week} />
      </section>

      {/* Streaks */}
      <section className="mt-10">
        <h2 className="mb-4 text-xs uppercase tracking-[0.3em] text-gold">Streaks</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StreakCard icon={<Flame size={18} />} label="Meditation" current={medStreak.current} longest={medStreak.longest} />
          <StreakCard icon={<Wind size={18} />} label="Breath" current={brStreak.current} longest={brStreak.longest} />
          <StreakCard icon={<BookOpen size={18} />} label="Reading" current={rdStreak.current} longest={rdStreak.longest} />
          <StreakCard icon={<Sparkles size={18} />} label="Activations" current={activationStreak.current} longest={activationStreak.longest} />
        </div>
      </section>

      {/* Log a practice */}
      <section className="mt-10">
        <h2 className="mb-4 text-xs uppercase tracking-[0.3em] text-gold">Log a practice</h2>
        <div className="grid gap-3 sm:grid-cols-3">
          <PracticeLogger kind="meditation" label="Meditation" icon={<Flame size={16} />} onSaved={load} />
          <PracticeLogger kind="breath" label="Breath" icon={<Wind size={16} />} onSaved={load} />
          <PracticeLogger kind="reading" label="Reading" icon={<BookOpen size={16} />} onSaved={load} />
        </div>
      </section>

      {/* Lifetime progress */}
      <section className="mt-14">
        <h2 className="mb-4 text-xs uppercase tracking-[0.3em] text-gold">Lifetime progress</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatTile icon={<Flame size={16} />} label="Meditation" value={medMinutes} unit="min" sub={`${meditation.length} sessions`} />
          <StatTile icon={<Wind size={16} />} label="Breath" value={brMinutes} unit="min" sub={`${breath.length} sessions`} />
          <StatTile icon={<BookOpen size={16} />} label="Reading" value={rdMinutes} unit="min" sub={`${reading.length} sessions`} />
          <StatTile icon={<Music2 size={16} />} label="Music" value={listening.length} unit="plays" sub={`${new Set(listening.map((l) => l.release_slug)).size} releases`} />
          <StatTile icon={<Sparkles size={16} />} label="Activations" value={completions.length} unit="done" sub="lifetime" />
          <StatTile icon={<GraduationCap size={16} />} label="Courses" value={courses.length} unit="finished" sub="lifetime" />
          <StatTile icon={<NotebookPen size={16} />} label="Journal" value={journals.length} unit="entries" sub="lifetime" />
          <StatTile icon={<Trophy size={16} />} label="Total XP" value={xp} unit="xp" sub={`Lv ${lotus.level}`} />
        </div>
      </section>

      {/* Journal */}
      <section className="mt-14">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xs uppercase tracking-[0.3em] text-gold">Journal</h2>
          <span className="text-xs text-muted-foreground">{journals.length} entries</span>
        </div>
        <JournalComposer onSaved={load} />
        {journals.length > 0 && (
          <ul className="mt-6 space-y-3">
            {journals.slice(0, 6).map((e) => (
              <li key={e.id} className="rounded-xl border hairline bg-charcoal p-5">
                <div className="flex items-baseline justify-between gap-3">
                  <p className="font-serif text-base text-gold-luminous">{e.title || "Untitled"}</p>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    {new Date(e.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground line-clamp-6">{e.body}</p>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Achievements */}
      <section className="mt-14">
        <h2 className="mb-4 text-xs uppercase tracking-[0.3em] text-gold">Achievements</h2>
        <UserBadges />
      </section>

      {/* Ecosystem links */}
      <section className="mt-14 grid gap-3 sm:grid-cols-3">
        <QuickLink to="/activations" title="Today's activation" icon={<Sparkles size={16} />} />
        <QuickLink to="/music" title="Explore music" icon={<Music2 size={16} />} />
        <QuickLink to="/account" title="Account & orders" icon={<CalendarCheck size={16} />} />
      </section>
    </div>
  );
}

/* ------------ subcomponents ------------ */

function StreakCard({ icon, label, current, longest }: { icon: React.ReactNode; label: string; current: number; longest: number }) {
  const active = current > 0;
  return (
    <div className={`rounded-xl border p-5 ${active ? "hairline bg-charcoal shadow-[0_0_0_1px_rgba(212,175,55,0.15)]" : "hairline bg-charcoal/60"}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold">
        <span className={active ? "text-gold-luminous" : "text-muted-foreground"}>{icon}</span> {label}
      </div>
      <p className="mt-3 font-serif text-3xl text-gold-luminous">
        {current}<span className="ml-1 text-xs text-muted-foreground">day{current === 1 ? "" : "s"}</span>
      </p>
      <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Best · {longest}</p>
    </div>
  );
}

function StatTile({ icon, label, value, unit, sub }: { icon: React.ReactNode; label: string; value: number; unit: string; sub: string }) {
  return (
    <div className="rounded-xl border hairline bg-charcoal p-5">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold">{icon}{label}</div>
      <p className="mt-3 font-serif text-2xl text-gold-luminous">
        {value.toLocaleString()}<span className="ml-1 text-xs text-muted-foreground">{unit}</span>
      </p>
      <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{sub}</p>
    </div>
  );
}

function PracticeLogger({ kind, label, icon, onSaved }: { kind: PracticeKind; label: string; icon: React.ReactNode; onSaved: () => void }) {
  const [minutes, setMinutes] = useState<string>("10");
  const [busy, setBusy] = useState(false);
  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        const m = parseInt(minutes, 10);
        if (!Number.isFinite(m) || m <= 0) return;
        setBusy(true);
        const { data: u } = await supabase.auth.getUser();
        if (u.user) {
          await supabase.from("practice_logs").insert({ user_id: u.user.id, kind, minutes: m });
        }
        setBusy(false);
        onSaved();
      }}
      className="rounded-xl border hairline bg-charcoal p-5"
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold">{icon}{label}</div>
      <div className="mt-3 flex items-center gap-2">
        <input
          type="number"
          inputMode="numeric"
          min={1}
          max={600}
          value={minutes}
          onChange={(e) => setMinutes(e.target.value)}
          className="w-20 rounded-lg border hairline bg-obsidian px-3 py-2 text-sm text-foreground focus:border-gold focus:outline-none"
          aria-label={`${label} minutes`}
        />
        <span className="text-xs text-muted-foreground">min</span>
        <button
          type="submit"
          disabled={busy}
          className="ml-auto inline-flex items-center gap-1 rounded-full bg-gold px-4 py-2 text-xs font-semibold text-obsidian hover:bg-gold-luminous disabled:opacity-50"
        >
          <Plus size={12} /> Log
        </button>
      </div>
    </form>
  );
}

function DailyIntentionCard({ intention, onSaved, today }: { intention: IntentionRow | null; onSaved: () => void; today: string }) {
  const [text, setText] = useState(intention?.intention ?? "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setText(intention?.intention ?? ""); }, [intention]);
  return (
    <div className="rounded-2xl border hairline bg-charcoal p-6">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold">
        <Sun size={14} /> Today's intention
      </div>
      <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
        {new Date(today).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
      </p>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="I move as the current, not against it."
        rows={2}
        className="mt-4 w-full resize-none rounded-lg border hairline bg-obsidian p-3 text-sm focus:border-gold focus:outline-none"
      />
      <button
        type="button"
        disabled={busy || text.trim().length === 0}
        onClick={async () => {
          setBusy(true);
          const { data: u } = await supabase.auth.getUser();
          if (u.user) {
            await supabase.from("daily_intentions").upsert(
              { user_id: u.user.id, intention_date: today, intention: text.trim() },
              { onConflict: "user_id,intention_date" },
            );
          }
          setBusy(false);
          onSaved();
        }}
        className="mt-3 inline-flex items-center gap-1 rounded-full bg-gold px-4 py-2 text-xs font-semibold text-obsidian hover:bg-gold-luminous disabled:opacity-50"
      >
        {intention ? "Update" : "Set intention"}
      </button>
    </div>
  );
}

function WeeklyReviewCard({ review, onSaved, weekStart }: { review: ReviewRow | null; onSaved: () => void; weekStart: string }) {
  const [wins, setWins] = useState(review?.wins ?? "");
  const [lessons, setLessons] = useState(review?.lessons ?? "");
  const [focus, setFocus] = useState(review?.focus_next ?? "");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setWins(review?.wins ?? "");
    setLessons(review?.lessons ?? "");
    setFocus(review?.focus_next ?? "");
  }, [review]);
  return (
    <div className="rounded-2xl border hairline bg-charcoal p-6">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold">
        <CalendarCheck size={14} /> Weekly review
      </div>
      <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
        Week of {new Date(weekStart).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
      </p>
      <div className="mt-4 grid gap-2">
        <textarea value={wins} onChange={(e) => setWins(e.target.value)} rows={2} placeholder="Wins — what rose?" className="w-full resize-none rounded-lg border hairline bg-obsidian p-3 text-sm focus:border-gold focus:outline-none" />
        <textarea value={lessons} onChange={(e) => setLessons(e.target.value)} rows={2} placeholder="Lessons — what taught you?" className="w-full resize-none rounded-lg border hairline bg-obsidian p-3 text-sm focus:border-gold focus:outline-none" />
        <textarea value={focus} onChange={(e) => setFocus(e.target.value)} rows={2} placeholder="Next — where does the current pull?" className="w-full resize-none rounded-lg border hairline bg-obsidian p-3 text-sm focus:border-gold focus:outline-none" />
      </div>
      <button
        type="button"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          const { data: u } = await supabase.auth.getUser();
          if (u.user) {
            await supabase.from("weekly_reviews").upsert(
              { user_id: u.user.id, week_start: weekStart, wins: wins.trim() || null, lessons: lessons.trim() || null, focus_next: focus.trim() || null },
              { onConflict: "user_id,week_start" },
            );
          }
          setBusy(false);
          onSaved();
        }}
        className="mt-3 inline-flex items-center gap-1 rounded-full bg-gold px-4 py-2 text-xs font-semibold text-obsidian hover:bg-gold-luminous disabled:opacity-50"
      >
        {review ? "Update review" : "Save review"}
      </button>
    </div>
  );
}

function JournalComposer({ onSaved }: { onSaved: () => void }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <div className="rounded-xl border hairline bg-charcoal p-5">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold">
        <NotebookPen size={14} /> New entry
      </div>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Title (optional)"
        className="mt-3 w-full rounded-lg border hairline bg-obsidian px-3 py-2 text-sm focus:border-gold focus:outline-none"
      />
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={4}
        placeholder="Speak. The page listens."
        className="mt-2 w-full resize-none rounded-lg border hairline bg-obsidian p-3 text-sm focus:border-gold focus:outline-none"
      />
      <button
        type="button"
        disabled={busy || body.trim().length === 0}
        onClick={async () => {
          setBusy(true);
          const { data: u } = await supabase.auth.getUser();
          if (u.user) {
            await supabase.from("journal_entries").insert({ user_id: u.user.id, title: title.trim() || null, body: body.trim() });
          }
          setTitle("");
          setBody("");
          setBusy(false);
          onSaved();
        }}
        className="mt-3 inline-flex items-center gap-1 rounded-full bg-gold px-4 py-2 text-xs font-semibold text-obsidian hover:bg-gold-luminous disabled:opacity-50"
      >
        Save entry
      </button>
    </div>
  );
}

function QuickLink({ to, title, icon }: { to: string; title: string; icon: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="group flex items-center justify-between rounded-xl border hairline bg-charcoal px-5 py-4 hover:border-gold"
    >
      <span className="inline-flex items-center gap-2 text-sm text-foreground group-hover:text-gold-luminous">
        <span className="text-gold">{icon}</span> {title}
      </span>
      <ChevronRight size={16} className="text-muted-foreground group-hover:text-gold" />
    </Link>
  );
}
